import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

const UpdateProduct = () => {
  const { id } = useParams();
  // console.log(id);
  const [formData, setFormData] = useState({
    gender: "",
    category: "",
    name: "",
    description: "",
    grossWeight: 0,
    goldKt: 0,
    goldColor: "",
    diamondWeight: 0,
    diamondColorAndClarity: "",
    height: 0,
    width: 0,
    length: 0,
    productId: "",
    image1: null,
    image2: null,
    image3: null,
    video: null,
  });

  const [imagePreview, setImagePreview] = useState({
    image1: null,
    image2: null,
    image3: null,
  });
  const [videoPreview, setVideoPreview] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const URL = process.env.REACT_APP_URL;

  const getCategoryOptions = () => {
    switch (formData.gender) {
      case "Man":
        return ["Bracelet", "Ring"];
      case "Woman":
        return [
          "Necklace",
          "Bracelet",
          "Earrings",
          "Pendant set",
          "Ring",
          "Mangalsutra",
          "Bengal",
          "Coupleband"
        ];
      default:
        return [];
    }
  };

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        const response = await axios.get(`${URL}products/${id}`);
        const product = response.data;
        // console.log(response.data.image1);
        setFormData({
          gender: product.gender || "",
          category: product.category || "",
          name: product.name || "",
          description: product.description || "",
          grossWeight: product.grossWeight || "",
          goldKt: product.goldKt || "",
          goldColor: product.goldColor || "",
          diamondWeight: product.diamondWeight || "",
          diamondColorAndClarity: product.diamondColorAndClarity || "",
          height: product.height || "",
          width: product.width || "",
          length: product.length || "",
          productId: product.productId || "",
          image1: product.image1 || null,
          image2: product.image2 || null,
          image3: product.image3 || null,
          video: product.video || null,
        });

        setImagePreview({
          image1: product.image1 ? `${URL}upload/${product.image1}` : null,
          image2: product.image2 ? `${URL}upload/${product.image2}` : null,
          image3: product.image3 ? `${URL}upload/${product.image3}` : null,
        });
        if (product.video) {
          setVideoPreview(`${URL}upload/${product.video}`);
        }
      } catch (error) {
        enqueueSnackbar("Error fetching product data", { variant: "error" });
      }
    };

    fetchProductData();
  }, [id, URL, enqueueSnackbar]);

  const validateForm = () => {
    let formErrors = {};
    if (!formData.gender) formErrors.gender = "Gender is required";
    if (!formData.category) formErrors.category = "Category is required";
    if (!formData.name) formErrors.name = "Product Name is required";
    if (!formData.description)
      formErrors.description = "Description is required";
    if (!formData.grossWeight)
      formErrors.grossWeight = "Gross Weight is required";
    if (!formData.diamondWeight)
      formErrors.diamondWeight = "Diamond Weight is required";
    if (!formData.diamondColorAndClarity)
      formErrors.diamondColorAndClarity =
        "Diamond Color and Clarity is required";
    if (!formData.height) formErrors.height = "Height is required";
    if (!formData.width) formErrors.width = "Width is required";
    if (!formData.length) formErrors.length = "Length is required";
    if (!formData.productId) formErrors.productId = "Product ID is required";

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (files && files.length > 0) {
      const file = files[0];

      if (name.startsWith("image")) {
        if (!file.type.startsWith("image/")) {
          enqueueSnackbar("Please upload a valid image file.", {
            variant: "error",
          });
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          enqueueSnackbar("File size must be less than 5MB.", {
            variant: "error",
          });
          return;
        }

        setFormData({ ...formData, [name]: file });

        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview((prev) => ({ ...prev, [name]: reader.result }));
        };
        reader.readAsDataURL(file);
      } else if (name === "video") {
        if (!file.type.startsWith("video/")) {
          enqueueSnackbar("Please upload a valid video file.", {
            variant: "error",
          });
          return;
        }
        if (file.size > 30 * 1024 * 1024) {
          enqueueSnackbar("Video size must be less than 40MB.", {
            variant: "error",
          });
          return;
        }

        setFormData({ ...formData, video: file });
        setVideoPreview(window.URL.createObjectURL(file)); // Video preview
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleRemoveFile = (key) => {
    setFormData((prevState) => ({
      ...prevState,
      [key]: null,
    }));
    setImagePreview((prevState) => ({
      ...prevState,
      [key]: null,
    }));
    if (key === "video") {
      setFormData((prev) => ({
        ...prev,
        video: null,
      }));
      setVideoPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    setIsSubmitting(true);

    const data = new FormData();

    Object.keys(formData).forEach((key) => {
      if (
        key !== "image1" &&
        key !== "image2" &&
        key !== "image3" &&
        key !== "video"
      ) {
        data.append(key, formData[key]);
      }
    });

    if (formData.image1 === null) {
      data.append("image1", null); // Send null when image is removed
    } else if (formData.image1) {
      data.append("image1", formData.image1);
    }

    if (formData.image2 === null) {
      data.append("image2", null); // Send null when image is removed
    } else if (formData.image2) {
      data.append("image2", formData.image2);
    }

    if (formData.image3 === null) {
      data.append("image3", null); // Send null when image is removed
    } else if (formData.image3) {
      data.append("image3", formData.image3);
    }

    if (formData.video === null) {
      data.append("video", null); // Send null if video is removed
    } else if (formData.video) {
      data.append("video", formData.video);
    }

    try {
      
      const response = await axios.post(`${URL}products/${id}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        enqueueSnackbar("Product updated successfully!", {
          variant: "success",
        });
        navigate("/products");
      } else {
    setIsSubmitting(false);

        enqueueSnackbar(response.statusText || "Failed to update product.", {
          variant: "error",
        });
      }
    } catch (error) {
    setIsSubmitting(false);

      enqueueSnackbar(
        error.response?.data?.message || "An error occurred. Please try again.",
        { variant: "error" }
      );
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h2 className="text-2xl font-semibold text-center mb-6">
        Update Diamond Jewelry
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="gender" className="block text-lg font-medium mb-2">
            Gender
          </label>
          <select
            id="gender"
            name="gender"
            className="w-full p-3 border border-gray-300 rounded-lg"
            value={formData.gender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Man">Man</option>
            <option value="Woman">Woman</option>
          </select>
          {errors.gender && <p className="text-red-500">{errors.gender}</p>}
        </div>

        {formData.gender && (
          <div className="mb-4">
            <label
              htmlFor="category"
              className="block text-lg font-medium mb-2"
            >
              Category
            </label>
            <select
              id="category"
              name="category"
              className="w-full p-3 border border-gray-300 rounded-lg"
              value={formData.category}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Category</option>
              {getCategoryOptions().map((category) => (
                <option key={category} value={category}>
                  {category == "Bengal" ? "Bangel" :category }
                </option>
              ))}
            </select>
            {errors.category && (
              <p className="text-red-500">{errors.category}</p>
            )}
          </div>
        )}

        {[
          { name: "name", label: "Product Name", type: "text" },
          { name: "description", label: "Description", type: "textarea" },
          { name: "grossWeight", label: "Gross Weight", type: "text" },
          { name: "goldKt", label: "Gold KT", type: "text" },
          { name: "goldColor", label: "Gold Color", type: "text" },
          { name: "diamondWeight", label: "Diamond Weight", type: "text" },
          {
            name: "diamondColorAndClarity",
            label: "Diamond Color and Clarity",
            type: "text",
          },
          { name: "height", label: "Product Dimension Height", type: "text" },
          { name: "width", label: "Product Dimension Width", type: "text" },
          { name: "length", label: "Chain Length", type: "text" },
          { name: "productId", label: "Product ID", type: "text" },
        ].map((field) => (
          <div className="mb-4" key={field.name}>
            <label
              htmlFor={field.name}
              className="block text-lg font-medium mb-2"
            >
              {field.label}
            </label>
            {field.type === "textarea" ? (
              <textarea
                id={field.name}
                name={field.name}
                className="w-full p-3 border border-gray-300 rounded-lg"
                value={formData[field.name]}
                onChange={handleInputChange}
                required
              />
            ) : (
              <input
                id={field.name}
                name={field.name}
                type={field.type}
                disabled={field.name === "productId"}
                className="w-full p-3 border border-gray-300 rounded-lg"
                value={formData[field.name]}
                onChange={handleInputChange}
                required
              />
            )}
            {errors[field.name] && (
              <p className="text-red-500">{errors[field.name]}</p>
            )}
          </div>
        ))}
        {["image1", "image2", "image3"].map((imageKey) => (
          <div className="mb-4" key={imageKey}>
            <label
              htmlFor={imageKey}
              className="block text-lg font-medium mb-2"
            >
              Upload {imageKey}
            </label>
            {!formData[imageKey] ? (
              <input
                type="file"
                id={imageKey}
                name={imageKey}
                className="w-full p-3 border border-gray-300 rounded-lg"
                accept="image/*"
                onChange={handleInputChange}
              />
            ) : (
              <div>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(imageKey)}
                  className="text-red-500"
                >
                  Remove {imageKey}
                </button>
                <div className="mt-2">
                  <img
                    src={imagePreview[imageKey]}
                    alt="Preview"
                    className="w-32 h-auto border border-gray-200"
                  />
                </div>
              </div>
            )}
{/* 
            {formData[imageKey] && (
              <p className="mt-1 text-gray-700">{formData[imageKey].name}</p>
            )} */}
          </div>
        ))}
        <div className="mb-4">
          <label htmlFor="video" className="block text-lg font-medium mb-2">
            Upload Video
          </label>
          {!formData.video ? (
            <input
              type="file"
              name="video"
              id="video"
              className="w-full p-3 border border-gray-300 rounded-lg"
              accept="video/*"
              onChange={handleInputChange}
            />
          ) : (
            <div>
              <button
                type="button"
                onClick={() => handleRemoveFile("video")}
                className="text-red-500"
              >
                Remove Video
              </button>
              <div className="mt-2">
                <video controls className="w-64 h-32">
                  <source src={videoPreview} type="video/mp4" />
                </video>
              </div>
            </div>
          )}
          {/* {formData.video && (
            <p className="mt-1 text-gray-700">{formData.video.name}</p>
          )} */}
        </div>
        <button
          type="submit"
          className={`px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            isSubmitting ? "opacity-50 cursor-not-allowed" : ""
          }`}
          disabled={isSubmitting} // Disable the button when submitting
        >
          {isSubmitting ? (
            <span className="animate-spin">Loading....</span>
          ) : (
            "Update Product"
          )}
        </button>
      </form>
    </div>
  );
};

export default UpdateProduct;
